import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LogIn, LogOut, Clock as ClockIcon } from 'lucide-react';
import { apiOrThrow } from '@/lib/api';
import { useGeolocation } from '@/hooks/useGeolocation';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Alert } from '@/components/ui/Alert';
import { Card, CardBody } from '@/components/ui/Card';
import { GpsStatusCard } from '@/components/clock/GpsStatusCard';
import { ClockEventList, type ClockEvent } from '@/components/clock/ClockEventList';
import { cn } from '@/lib/cn';

interface CurrentStatus {
  status: 'in' | 'out';
  since: string | null;
  lastEvent: {
    id: string;
    eventType: 'clock_in' | 'clock_out';
    serverTs: string;
    gps: { lat: number; lng: number; accuracy: number };
    cfCity: string | null;
    cfCountry: string | null;
  } | null;
}

export function ClockPage() {
  const qc = useQueryClient();
  const geo = useGeolocation({ autoRequest: true, enableHighAccuracy: true, maximumAgeMs: 0 });

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [note, setNote] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Current clocked-in status
  const currentQuery = useQuery({
    queryKey: ['clock', 'current'],
    queryFn: () => apiOrThrow<CurrentStatus>('/clock/current'),
    staleTime: 10_000,
  });

  // Today's events
  const todayQuery = useQuery({
    queryKey: ['clock', 'today'],
    queryFn: () => apiOrThrow<{ items: ClockEvent[] }>('/clock/today'),
    staleTime: 10_000,
  });

  const isCurrentlyIn = currentQuery.data?.status === 'in';
  const nextAction: 'clock_in' | 'clock_out' = isCurrentlyIn ? 'clock_out' : 'clock_in';

  const submitMutation = useMutation({
    mutationFn: async () => {
      // Always refresh GPS right before submit — coords might be stale.
      const fresh = await geo.refresh();
      if (!fresh) {
        throw new Error('Location is required. Please grant access and try again.');
      }
      return apiOrThrow<{
        id: string;
        eventType: 'clock_in' | 'clock_out';
        serverTs: string;
      }>('/clock', {
        method: 'POST',
        json: {
          eventType: nextAction,
          clientTs: new Date().toISOString(),
          gps: { lat: fresh.lat, lng: fresh.lng, accuracy: fresh.accuracy },
          note: note.trim() || undefined,
        },
      });
    },
    onSuccess: () => {
      setConfirmOpen(false);
      setNote('');
      setSubmitError(null);
      qc.invalidateQueries({ queryKey: ['clock'] });
    },
    onError: (e: Error) => setSubmitError(e.message),
  });

  const canAct = geo.status === 'granted' && geo.coords !== null && !currentQuery.isPending;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-brand-charcoal">Clock-in / out</h1>
        <p className="mt-1 text-sm text-brand-gray-mid">
          Location is captured on every event for audit purposes.
        </p>
      </div>

      <StatusCard
        isIn={isCurrentlyIn}
        since={currentQuery.data?.since ?? null}
        loading={currentQuery.isPending}
      />

      <GpsStatusCard
        status={geo.status}
        coords={geo.coords}
        errorMessage={geo.errorMessage}
        onRefresh={() => void geo.refresh()}
      />

      <div>
        <Button
          size="lg"
          fullWidth
          variant={nextAction === 'clock_in' ? 'primary' : 'danger'}
          disabled={!canAct}
          onClick={() => {
            setSubmitError(null);
            setConfirmOpen(true);
          }}
        >
          {nextAction === 'clock_in' ? (
            <>
              <LogIn className="h-4 w-4" aria-hidden />
              <span>Clock IN</span>
            </>
          ) : (
            <>
              <LogOut className="h-4 w-4" aria-hidden />
              <span>Clock OUT</span>
            </>
          )}
        </Button>
        {!canAct && (
          <p className="mt-2 text-center text-xs text-brand-gray-mid">
            Waiting for GPS before this button activates.
          </p>
        )}
      </div>

      <ClockEventList
        title="Today's events"
        events={todayQuery.data?.items ?? []}
        loading={todayQuery.isPending}
        empty="No clock-in / out events today yet."
      />

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={`Confirm ${nextAction === 'clock_in' ? 'Clock IN' : 'Clock OUT'}`}
        description="Your current location and the server timestamp will be recorded."
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmOpen(false)} type="button">
              Cancel
            </Button>
            <Button
              variant={nextAction === 'clock_in' ? 'primary' : 'danger'}
              loading={submitMutation.isPending}
              onClick={() => submitMutation.mutate()}
              type="button"
            >
              Confirm
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm">
          {geo.coords && (
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 rounded bg-brand-off-white p-3 font-mono text-xs">
              <dt className="text-brand-gray-mid">Latitude</dt>
              <dd>{geo.coords.lat.toFixed(6)}</dd>
              <dt className="text-brand-gray-mid">Longitude</dt>
              <dd>{geo.coords.lng.toFixed(6)}</dd>
              <dt className="text-brand-gray-mid">Accuracy</dt>
              <dd>±{Math.round(geo.coords.accuracy)}m</dd>
              <dt className="text-brand-gray-mid">Time</dt>
              <dd>{new Date().toLocaleTimeString()}</dd>
            </dl>
          )}
          <div>
            <label htmlFor="clock-note" className="block text-sm font-medium text-brand-charcoal">
              Note (optional)
            </label>
            <textarea
              id="clock-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. on-site at client ACME"
              maxLength={500}
              rows={2}
              className="mt-1 w-full rounded border border-brand-gray-light bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue"
            />
          </div>
          {submitError && <Alert tone="danger">{submitError}</Alert>}
        </div>
      </Modal>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status card — shows currently in/out + elapsed time
// ---------------------------------------------------------------------------

function StatusCard({
  isIn,
  since,
  loading,
}: {
  isIn: boolean;
  since: string | null;
  loading: boolean;
}) {
  const [elapsed, setElapsed] = useState<string>('—');
  useEffect(() => {
    if (!since || !isIn) {
      setElapsed('—');
      return;
    }
    const update = () => {
      const ms = Date.now() - new Date(since).getTime();
      const s = Math.max(0, Math.floor(ms / 1000));
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      const sec = s % 60;
      setElapsed(
        `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`,
      );
    };
    update();
    const t = setInterval(update, 1000);
    return () => clearInterval(t);
  }, [since, isIn]);

  return (
    <Card>
      <CardBody>
        <div className="flex items-center gap-4">
          <div
            className={cn(
              'flex h-14 w-14 items-center justify-center rounded-full',
              isIn ? 'bg-brand-success/15 text-brand-success' : 'bg-brand-gray-light text-brand-gray-mid',
            )}
          >
            <ClockIcon className="h-7 w-7" aria-hidden />
          </div>
          <div className="flex-1">
            <p className="text-xs uppercase tracking-wider text-brand-gray-mid">
              Current status
            </p>
            <p
              className={cn(
                'text-xl font-semibold',
                isIn ? 'text-brand-success' : 'text-brand-charcoal',
              )}
            >
              {loading ? 'Loading…' : isIn ? 'Clocked IN' : 'Not clocked in'}
            </p>
            {isIn && since && (
              <p className="mt-0.5 text-sm text-brand-gray-mid">
                Since {new Date(since).toLocaleTimeString()} · elapsed{' '}
                <span className="font-mono">{elapsed}</span>
              </p>
            )}
          </div>
        </div>
      </CardBody>
    </Card>
  );
}
